import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, Loader2, AlertTriangle, RefreshCw, ShieldCheck, Users, ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { money } from '@/data/constants';
import { track } from '@/lib/tracking';

import {
  SINGLE_LEAD_PRICE,
  STRIPE_ACCOUNT_ID,
  STRIPE_PUBLISHABLE_KEY,
  createLeadIntent,
  deviceLabel,
  rememberIntent,
  settleLeadPayment,
  LeadSettlement,
} from '@/lib/singleLead';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * $29 SINGLE LEAD — inline card payment (Stripe Elements).
 * ───────────────────────────────────────────────────────────────────────────
 *
 * MOBILE PAY BUTTON — what was wrong and what this component guarantees.
 *
 * The old lead flow had no pay button at all, and the PLAN flow called
 * `window.open()` AFTER an await. Mobile Safari and Chrome on Android only
 * allow a new window inside the synchronous part of a user gesture, so the
 * call was silently swallowed: the tap "did nothing". Nothing here opens a
 * window at all — the card form is inline and `redirect: 'if_required'` keeps
 * the customer on the page.
 *
 * Every failure mode the QA list called out is handled explicitly:
 *   • one submit at a time, guarded by a ref (set synchronously) so the FIRST
 *     tap always registers and the 2nd..nth are ignored — a state-only guard
 *     can lose the race on a fast double tap
 *   • the button is a real <button type="submit"> inside a <form>, 48px tall,
 *     with `touch-action: manipulation` so iOS does not wait for a double-tap
 *     zoom gesture before firing
 *   • an aria-live status line shows "Processing payment…", then a clear
 *     approved / declined / retry message. The customer is NEVER left on the
 *     same screen with no response
 *   • a 40s watchdog turns a hung request into a retryable error instead of a
 *     spinner that never resolves
 *   • errors scroll themselves into view (mobile keyboards cover the bottom
 *     half of the viewport, which is how the original decline message ended up
 *     off-screen)
 *   • settlement is idempotent server-side, so an interrupted network, a
 *     reload, or a retry can never double charge or double credit
 */

const stripePromise =
  STRIPE_ACCOUNT_ID && STRIPE_PUBLISHABLE_KEY
    ? loadStripe(STRIPE_PUBLISHABLE_KEY, { stripeAccount: STRIPE_ACCOUNT_ID })
    : null;

type Phase = 'idle' | 'processing' | 'settling' | 'done' | 'error';

interface FormProps {
  listingId: string;
  slug: string;
  paymentIntentId: string;
  onSettled: (s: LeadSettlement) => void;
}

const PayForm: React.FC<FormProps> = ({ listingId, slug, paymentIntentId, onSettled }) => {
  const { t, lang } = useI18n();
  const stripe = useStripe();
  const elements = useElements();
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const errorRef = useRef<HTMLDivElement | null>(null);

  // Bring any decline / validation message into view on small screens.
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const fail = (msg: string) => {
    submitting.current = false;
    setPhase('error');
    setError(msg);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Ref guard is set synchronously — the first tap always wins, repeats are
    // dropped. No duplicate charge is possible from repeated taps.
    if (submitting.current) return;
    if (!stripe || !elements) {
      fail(t('pay.formNotReady'));
      return;
    }
    submitting.current = true;
    setError('');
    setPhase('processing');
    track('cta_click', { action: 'pay_single_lead', device: deviceLabel(), listing: listingId });

    // Watchdog: never leave the customer staring at a spinner.
    const watchdog = window.setTimeout(() => {
      if (submitting.current) fail(t('pay.timeout'));
    }, 40000);

    try {
      rememberIntent({ listingId, slug, paymentIntentId });

      const { error: payError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      window.clearTimeout(watchdog);

      if (payError) {
        // Card declined / incorrect details / expired card / validation.
        fail(payError.message || t('pay.declined'));
        return;
      }
      if (!paymentIntent || paymentIntent.status !== 'succeeded') {
        fail(t('pay.notCompleted'));
        return;
      }

      // Paid. Settlement is idempotent, so retry on a flaky connection.
      setPhase('settling');
      let settled = await settleLeadPayment(paymentIntent.id, listingId, lang);
      if (!settled.ok) settled = await settleLeadPayment(paymentIntent.id, listingId, lang);

      submitting.current = false;
      if (!settled.ok) {
        fail(t('pay.settleRetry'));
        return;
      }
      setPhase('done');
      onSettled(settled);
    } catch (err) {
      window.clearTimeout(watchdog);
      fail((err as Error)?.message || t('pay.networkError'));
    }
  };

  const busy = phase === 'processing' || phase === 'settling';

  return (
    <form onSubmit={handleSubmit} noValidate>
      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <div
          ref={errorRef}
          role="alert"
          className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          <p className="flex items-start gap-2 font-bold">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </p>
          <p className="mt-1.5 text-xs leading-relaxed text-red-700">{t('pay.noChargeOnDecline')}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || busy}
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        className="mt-4 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3.5 text-base font-bold text-white transition-colors hover:bg-orange-600 focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-300 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" />}
        {/* The settling phase ADDS A CREDIT to the balance — it does not unlock
            anything, so the label must not promise an unlock the server will
            deliberately not perform. */}
        {phase === 'processing'
          ? t('pay.processingPayment')
          : phase === 'settling'
          ? t('pay.addingCredit')
          : `${t('pay.payNow')} — ${money(SINGLE_LEAD_PRICE)}`}
      </button>

      {/* Immediate, screen-reader-announced feedback for every tap. */}
      <p aria-live="polite" className="mt-2 min-h-[18px] text-center text-xs font-semibold text-slate-500">
        {phase === 'processing'
          ? t('pay.processingPayment')
          : phase === 'settling'
          ? t('pay.addingCredit')
          : phase === 'error'
          ? t('pay.tryAgain')
          : ''}
      </p>


      {phase === 'error' && (
        <button
          type="submit"
          className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-slate-300 px-4 py-3 text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" /> {t('pay.retry')}
        </button>
      )}

      <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-slate-400">
        <ShieldCheck className="h-3.5 w-3.5" /> {t('pay.securedBy')}
      </p>
    </form>
  );
};

interface Props {
  listingId: string;
  slug: string;
  /** Called after a successful settlement (unlocked or credit-only). */
  onSettled: (s: LeadSettlement) => void;
  /**
   * Where to send the customer when the one-off card path cannot be opened.
   * Memberships run on a different, hosted processor, so a disabled in-app
   * card path must NOT become a dead end — it routes to a purchase that works.
   */
  plansHref?: string;
  onLeavePage?: () => void;
}

/**
 * Loads a PaymentIntent for THIS lead and renders the inline card form.
 * Renders an honest, actionable message for every non-payable state instead of
 * an endless spinner.
 */
const SingleLeadPayment: React.FC<Props> = ({ listingId, slug, onSettled, plansHref, onLeavePage }) => {
  const { t, lang } = useI18n();
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [state, setState] = useState<'loading' | 'ready' | 'sold_out' | 'owned' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [offerPlans, setOfferPlans] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setState('loading');
    setMessage('');
    setOfferPlans(false);
    (async () => {
      const res = await createLeadIntent(listingId, lang);
      if (!active) return;
      if (res.alreadyOwned) {
        setState('owned');
        return;
      }
      if (res.soldOut) {
        setState('sold_out');
        return;
      }
      if (!res.clientSecret || !res.paymentIntentId) {
        setState('error');
        /*
          PROVIDER-LEVEL BLOCK vs A ONE-OFF GLITCH.

          When the payment provider has in-app card payments switched off (the
          gateway answers 410 `in_app_payments_disabled`) or the connected
          account is not finished onboarding, retrying will never succeed. The
          raw string ("gateway_410") means nothing to a contractor, so it is
          replaced with the plain-language setup notice and — critically — a
          link to the memberships, which run on a hosted processor and DO take
          payment. A transient failure still shows Retry as before.
        */
        const raw = `${res.error || ''} ${res.message || ''}`.toLowerCase();
        const providerOff =
          res.error === 'payments_not_configured' ||
          raw.includes('in_app_payments_disabled') ||
          raw.includes('gateway_410') ||
          raw.includes('hosted checkout');
        setOfferPlans(providerOff);
        setMessage(providerOff ? t('pay.setup') : res.message || t('pay.intentFailed'));
        return;
      }
      setClientSecret(res.clientSecret);
      setPaymentIntentId(res.paymentIntentId);
      setState('ready');
    })();
    return () => {
      active = false;
    };
  }, [listingId, lang, attempt, t]);

  const options = useMemo(
    () => ({ clientSecret, appearance: { theme: 'stripe' as const } }),
    [clientSecret]
  );

  /** Plans link shown whenever the one-off card path is unavailable. */
  const plansLink = plansHref ? (
    <Link
      to={plansHref}
      onClick={onLeavePage}
      className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white hover:bg-slate-800"
    >
      {t('pay.seePlans')} <ArrowRight className="h-4 w-4" />
    </Link>
  ) : null;

  if (!stripePromise) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900">
        {t('pay.setup')}
        {plansLink}
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <p className="flex items-center justify-center gap-2 rounded-xl bg-slate-50 px-4 py-4 text-sm font-semibold text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> {t('pay.loadingForm')}
      </p>
    );
  }

  if (state === 'sold_out') {
    return (
      <div className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-700">
        <p className="flex items-center gap-2 font-extrabold uppercase tracking-wide">
          <Users className="h-4 w-4" /> {t('leads.soldOutTitle')}
        </p>
        <p className="mt-2 leading-relaxed">{t('pay.soldOutNoCharge')}</p>
      </div>
    );
  }

  if (state === 'owned') {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm font-semibold text-emerald-800">
        {t('pay.alreadyOwned')}
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div
        className={`rounded-xl border px-4 py-4 text-sm ${
          offerPlans ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-red-200 bg-red-50 text-red-800'
        }`}
      >
        <p className="font-bold">{message || t('pay.intentFailed')}</p>
        {/* A provider-level block cannot be retried away — offer the path that
            actually takes money instead of a button that will fail again. */}
        {offerPlans ? (
          plansLink
        ) : (
          <button
            type="button"
            onClick={() => setAttempt((a) => a + 1)}
            style={{ touchAction: 'manipulation' }}
            className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border-2 border-red-300 px-4 py-2 text-sm font-bold text-red-800 hover:bg-red-100"
          >
            <RefreshCw className="h-4 w-4" /> {t('pay.retry')}
          </button>
        )}
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PayForm listingId={listingId} slug={slug} paymentIntentId={paymentIntentId} onSettled={onSettled} />
    </Elements>
  );
};

export default SingleLeadPayment;
